/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var baseTippyOptions = {
  delay: [100, 300],
  animation: 'shift-away-subtle',
  allowHtml: true
};

var baseTippySingletonOptions = {
  moveTransition: 'transform 0.2s ease-out',
  overrides: ['theme', 'delay', 'animation', 'allowHtml']
}

class ProductBadgeList extends HTMLDivElement {
  constructor() {
    super();

    this.tippyOptions = Object.assign({}, baseTippySingletonOptions, {
      overrides: baseTippySingletonOptions.overrides.concat('hideOnClick')
    });

    this.tippies = [];
    this.singleton = null;
  }

  connectedCallback() {
    this.createSingleton();
  }

  disconnectedCallback() {
    this.destroySingleton();
  }

  createSingleton() {
    if (this.singleton != null) {
      console.warn('Element already has tippy singleton instance [' + this.singleton.id + '], skipping instanciation');
      return;
    }

    this.tippies = [];
    this.singleton = tippy.createSingleton(this.tippies, this.tippyOptions);
  }

  destroySingleton() {
    if (this.singleton == null) {
      console.warn('Element does not have tippy singleton instance, skipping destroy');
      return;
    }

    this.singleton.destroy();
    this.singleton = null;
    this.tippies = null;
  }

  addTippy(tippy) {
    if (this.singleton == null) {
      console.warn('Element does not have tippy singleton instance, skipping add');
      return;
    }

    this.tippies = this.tippies.concat(tippy);
    this.singleton.setInstances(this.tippies);
  }

  removeTippy(tippy) {
    if (this.singleton == null) {
      console.warn('Element does not have tippy singleton instance, skipping add');
      return;
    }

    this.tippies = this.tippies.filter(function(tippyInSingleton) {
      return tippy !== tippyInSingleton;
    });
    this.singleton.setInstances(this.tippies);
  }
}
window.customElements.define('product-badge-list', ProductBadgeList, { extends: 'div' });

class ProductBadge extends HTMLSpanElement {
  constructor() {
    super();

    this.tippyOptions = Object.assign({}, baseTippyOptions, {
      theme: 'badge',
      hideOnClick: false
    });

    this.tippySingleton = null;
  }
  connectedCallback() {
    this.bindSingleton();
    this.createTippy();
  }
  disconnectedCallback() {
    this.destroyTippy();
    this.unbindSingleton();
  }
  bindSingleton() {
    this.tippySingleton = this.closest('[is="product-badge-list"]');
  }
  unbindSingleton() {
    this.tippySingleton = null;
  }
  createTippy() {
    if (this._tippy != null) {
      console.warn('Element already has tippy instance [' + this._tippy.id + '], skipping instanciation');
      return;
    }

    if (this.tippySingleton == null) {
      console.error('Element [' + this._tippy.id + '] singleton parent not found, skipping instanciation');
      return;
    }

    tippy(this, this.tippyOptions);
    // Will grab content from data attribute
    // Will auto set this._tippy

    this.tippySingleton.addTippy(this._tippy);
  }
  destroyTippy() {
    if (this._tippy == null) {
      console.warn('Element does not have tippy instance, skipping destroy');
      return;
    }

    if (this.tippySingleton == null) {
      console.warn('Element [' + this._tippy.id + '] singleton parent not found, skipping remove');
    } else {
      this.tippySingleton.removeTippy(this._tippy);
    }

    this._tippy.destroy();
    // Will auto unset this._tippy
    this._tippy = null;
  }
  isSameTippy(tippy) {
    return (this._tippy === tippy);
  }
  isNotSameTippy(tippy) {
    return !this.isSameTippy(tippy);
  }
}
window.customElements.define('product-badge', ProductBadge, { extends: 'span' });

var trustpilotWidgets = {
  'micro-count': '5419b6a8b0d04a076446a9ad'
}

var baseTrustpilotOptions = {
  template: trustpilotWidgets['micro-count'],
  theme: 'light',
  businessUnit: window.themeVariables.settings.misc_tp_bu_id,
  slug: window.themeVariables.settings.misc_tp_comp_slug,
  locale: window.themeVariables.shop.locale
}

class TrustpilotWidget extends HTMLDivElement {
  static get observedAttributes() {
    return [
      'template-id',
      'theme',
      'businessunit-id',
      'slug',
      'locale'
    ];
  }

  constructor() {
    super();
    this.fallback = this.innerHTML;
    console.log('ctor')
  }
  connectedCallback() {
    this.reloadContent();
    this.rebindTrustpilot();
    console.log('connected')
  }
  disconnectedCallback() {
    console.log('disconnected')
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.reloadContent();
    this.rebindTrustpilot();
    console.log('changed')
  }
  reloadContent() {
    var options = this.getOptions();

    this.innerHTML = `
    <!-- TrustBox widget -->
    <div
      class="trustpilot-widget"
      data-locale="${options.locale}"
      data-template-id="${options.template}"
      data-businessunit-id="${options.businessUnit}"
      data-style-height="24px"
      data-style-width="100%"
      data-theme="${options.theme}"
    >
      ${this.fallback}
    </div>
    <!-- End TrustBox widget -->
  `;
  }
  rebindTrustpilot() {
    var widgetElement = this.getWidgetContainer();

    if (widgetElement != null && window.Trustpilot != null) {
      window.Trustpilot.loadFromElement(widgetElement);
    }
  }
  getOptions() {
    var attributeOptions = this.getAttributesOptions();

    var mergedOptions = Object.assign({}, baseTrustpilotOptions, {
      ...(attributeOptions.template != null ? { template: attributeOptions.template } : {}),
      ...(attributeOptions.theme != null ? { theme: attributeOptions.theme } : {}),
      ...(attributeOptions.businessUnit != null ? { businessUnit: attributeOptions.businessUnit } : {}),
      ...(attributeOptions.slug != null ? { slug: attributeOptions.slug } : {}),
      ...(attributeOptions.locale != null ? { locale: attributeOptions.locale } : {}),
    });

    return mergedOptions;
  }
  getAttributesOptions() {
    return {
      template: this.getTemplate(this.getOptionalAttribute('template-id')),
      theme: this.getOptionalAttribute('theme'),
      businessUnit: this.getOptionalAttribute('businessunit-id'),
      slug: this.getOptionalAttribute('slug'),
      locale: this.getOptionalAttribute('locale')
    }
  }
  getOptionalAttribute (name) {
    return ((this.attributes[name] != null)
      ? this.attributes[name].value
      : null
    )
  }
  getWidgetContainer() {
    return this.querySelector('.trustpilot-widget')
  }
  getTemplate(key) {
    if (key != null && trustpilotWidgets[key] != null) {
      return trustpilotWidgets[key];
    } else {
      return key;
    }
  }
}
window.customElements.define('trustpilot-widget', TrustpilotWidget, { extends: 'div' });

// window.Trustpilot.loadFromElement(trustbox)

// ------------------------------
// START SNOW
// ------------------------------

if (window.themeVariables.settings.effects_snow_enabled && window.Sparticles != null) {
  window.onload = function() {
    var parallax = window.themeVariables.settings.effects_snow_parallax ? 5 : 1;
    var glow = window.themeVariables.settings.effects_snow_glow ? 10 : 0;
    var baseSpeed = window.themeVariables.settings.effects_snow_speed;
    var speed = window.themeVariables.settings.effects_snow_parallax ? baseSpeed : baseSpeed + 4;
    var minSize = window.themeVariables.settings.effects_snow_glow ? 6 : 2;
    var maxSize = window.themeVariables.settings.effects_snow_radius + (
      window.themeVariables.settings.effects_snow_glow ? 5 : 0
    );

    var wind = window.themeVariables.settings.effects_snow_wind;
    var direction = 240 - ((wind + 6) * 10)

    var maxAlpha = window.themeVariables.settings.effects_snow_opacity;
    var minAlpha = maxAlpha / 2;

    var sparticlesEl = document.querySelector(".snow-sparticles-container");
    var sparticles = new window.Sparticles(sparticlesEl, {
      density: window.themeVariables.settings.effects_snow_density,

      // Shape
      shape: "circle",
      minSize: minSize,
      maxSize: maxSize,
      minAlpha: minAlpha,
      maxAlpha: maxAlpha,
      alphaSpeed: 2,
      alphaVariance: 2,
      twinkle: false,
      color:["#ffffff"],
      glow: glow,

      // Position
      speed: speed,
      parallax: parallax,
      drift: 4,
      xVariance: 8,
      yVariance: 2,
      rotate: false,
      direction: direction
    });
  }
}

// ------------------------------
// END SNOW
// ------------------------------
